import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { INSTAGRAM_URL } from "../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EnquiryForm = () => {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please choose a service");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Inquiry sent — the Haus will be in touch soon.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong — please try again or DM us on Instagram.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "rounded-none border-0 border-b border-[#332A24] bg-transparent px-0 py-6 text-base font-light text-[#E6D5C3] placeholder:text-[#A89B8F]/50 focus-visible:border-[#E6D5C3] focus-visible:ring-0 focus-visible:ring-offset-0";

  return (
    <section id="enquire" data-testid="enquiry-section" className="bg-[#1A1512] py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="text-xs uppercase tracking-[0.35em] text-[#C8A88B]">
            Booking inquiries
          </span>
          <h2 className="mt-6 font-serif-display text-5xl font-light leading-[1.05] tracking-tight text-[#E6D5C3] lg:text-6xl">
            Save your
            <br />
            <em className="text-[#C8A88B]">spot.</em>
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-[#A89B8F]">
            Leave your details and the service you're interested in — your inquiry
            goes straight to the Haus and you'll hear back personally. Prefer to
            chat first?{" "}
            <a
              data-testid="enquiry-instagram-dm-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#E6D5C3] underline underline-offset-4 transition-colors duration-200 hover:text-[#DEBFA2]"
            >
              DM us on Instagram
            </a>
            .
          </p>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="space-y-10 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
            <div>
              <label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.25em] text-[#A89B8F]">
                Name
              </label>
              <Input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Your name"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.25em] text-[#A89B8F]">
                Email
              </label>
              <Input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="you@email.com"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-[#A89B8F]">
              I'm interested in
            </label>
            <Select
              value={form.service}
              onValueChange={(value) => setForm({ ...form, service: value })}
            >
              <SelectTrigger
                data-testid="enquiry-service-select"
                className="mt-2 rounded-none border-0 border-b border-[#332A24] bg-transparent px-0 py-6 text-base font-light text-[#E6D5C3] focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-[#A89B8F]/50"
              >
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#332A24] bg-[#241D1A] text-[#E6D5C3]">
                <SelectItem data-testid="service-option-extensions" value="Lash Extensions">
                  Lash Extensions
                </SelectItem>
                <SelectItem data-testid="service-option-lift" value="Lash Lift">
                  Lash Lift
                </SelectItem>
                <SelectItem data-testid="service-option-unsure" value="Not sure yet">
                  Not sure yet
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label htmlFor="enquiry-message" className="text-xs uppercase tracking-[0.25em] text-[#A89B8F]">
              Message
            </label>
            <Textarea
              id="enquiry-message"
              data-testid="enquiry-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Anything we should know? Preferred days, the look you're after..."
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </div>
          <button
            data-testid="enquiry-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#E6D5C3] px-8 py-5 text-xs uppercase tracking-[0.25em] text-[#130F0D] transition-colors duration-200 hover:bg-[#DEBFA2] disabled:opacity-50 sm:w-auto"
          >
            {submitting ? "Sending..." : "Send Inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
